import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import './NewItemModal.css'; // Budeme potřebovat i CSS

// Debounce hook pro efektivní vyhledávání
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

const NewItemModal = ({ isOpen, onClose, onSave }) => {
    const initialState = useMemo(() => ({
        Product_Name: '',
        Product_Code: '',
        Description: '',
        Item_Notes: '',
        Quantity: 1,
        List_Price: 0,
        Weight: 0,
        Delivery_Time: '',
        Origin: '',
        HS_Code: ''
    }), []);

    const [itemData, setItemData] = useState(initialState);
    
    // Stavy pro vyhledávání
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Reset stavu při otevření/zavření modálu
    useEffect(() => {
        if (isOpen) {
            setItemData(initialState);
            setSearchTerm('');
            setResults([]);
        }
    }, [isOpen, initialState]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setItemData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        if (!itemData.Product_Name) {
            toast.error("Product Name is required.");
            return;
        }
        onSave(itemData);
        onClose();
    };

    const searchProducts = useCallback(async (term) => {
        if (term.length < 2) {
            setResults([]);
            return;
        }
        setIsLoading(true);
        try {
            const response = await fetch('/server/quote_service/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: term })
            });
            if (!response.ok) throw new Error('Search failed');
            const data = await response.json();
            setResults(data);
        } catch (error) {
            console.error("Error searching products:", error);
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        searchProducts(debouncedSearchTerm);
    }, [debouncedSearchTerm, searchProducts]);

    const handleProductSelect = (product) => {
        // Předvyplníme formulář daty z vybraného produktu
        setItemData({
            Product_Name: product.Product_Name || '',
            Product_Code: product.Product_Code || '',
            Description: product.Description || '',
            Item_Notes: '',
            Quantity: 1,
            List_Price: product.Unit_Price || 0,
            Weight: product.Weight || 0,
            Delivery_Time: '',
            Origin: product.Origin || '',
            HS_Code: product.HS_Code || ''
        });
        // Vyčistíme výsledky vyhledávání
        setSearchTerm('');
        setResults([]);
        toast.info(`Form pre-filled with data from "${product.Product_Name}".`);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content large" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Add New Item</h2>
                    <button onClick={onClose} className="close-button">×</button>
                </div>
                <div className="modal-body new-item-layout">
                    
                    <div className="form-section">
                        <h3>Item Details</h3>
                        <div className="form-grid">
                            <label>Product Name*</label>
                            <input type="text" name="Product_Name" value={itemData.Product_Name} onChange={handleChange} />
                            
                            <label>Product Code</label>
                            <input type="text" name="Product_Code" value={itemData.Product_Code} onChange={handleChange} />
                            
                            <label>Description</label>
                            <textarea name="Description" value={itemData.Description} onChange={handleChange} rows="4"></textarea>
                            
                            <label>Item Notes</label>
                            <textarea name="Item_Notes" value={itemData.Item_Notes} onChange={handleChange} rows="2"></textarea>
                            
                            <label>Quantity</label>
                            <input type="number" name="Quantity" value={itemData.Quantity} onChange={handleChange} />
                            
                            <label>List Price (EUR)</label>
                            <input type="number" name="List_Price" value={itemData.List_Price} onChange={handleChange} />
                            
                            <label>Weight (kg)</label>
                            <input type="number" name="Weight" value={itemData.Weight} onChange={handleChange} />
                            
                            <label>Lead Time (wd)</label>
                            <input type="number" name="Delivery_Time" value={itemData.Delivery_Time} onChange={handleChange} />
                            
                            <label>Origin</label>
                            <input type="text" name="Origin" value={itemData.Origin} onChange={handleChange} />
                            
                            <label>HS Code</label>
                            <input type="text" name="HS_Code" value={itemData.HS_Code} onChange={handleChange} />
                        </div>
                    </div>
                    
                    <div className="search-section">
                        <h3>Clone from existing product...</h3>
                        <input
                            type="text"
                            placeholder="Type to search..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <div className="results-container">
                            {isLoading && <div className="loading-indicator">Searching...</div>}
                            {!isLoading && results.length > 0 && (
                                results.map(product => (
                                    <div 
                                        key={product.id} 
                                        className="result-item"
                                        onClick={() => handleProductSelect(product)}
                                        title="Click to pre-fill the form"
                                    >
                                        <div className="product-name">{product.Product_Name}</div>
                                        <div className="product-code">{product.Product_Code}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn-secondary">Cancel</button>
                    <button onClick={handleSave} className="btn-success">Save Item</button>
                </div>
            </div>
        </div>
    );
};

export default NewItemModal;